import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import { whatIfSetup } from "@/libs/tournamentSetupApi";
import type { SetupDay, SetupWhatIfResult, TimeSettings, TournamentSetupView } from "@/libs/tournamentSetupApi";

import { formatDay, formatTime } from "../setup/fields";
import { TermHelp } from "../setup/term-help";
import { bandText, CloseIcon, DenseButton, DenseInput, Mono, Segmented, SetupSurface, tone } from "../setup/ui";
import { useSetupCommand } from "./use-setup-command";

const DEFAULTS: TimeSettings = {
  warmup_minutes: 3, changeover_minutes: 5, singles_match_minutes: 30, doubles_match_minutes: 35,
  minimum_rest_minutes: 20, count_basis: "limits",
};

function Section({ title, children, id }: { title?: ReactNode; children: ReactNode; id?: string }) {
  return (
    <Box sx={{ p: "12px 18px", borderTop: `1px solid ${tone.divider}`, "&:first-of-type": { borderTop: 0 } }}>
      {title ? (
        <Box id={id} sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".3px", color: tone.subtle, mb: 1, display: "flex", alignItems: "center", gap: "5px" }}>
          {title}
        </Box>
      ) : null}
      {children}
    </Box>
  );
}

function NumberField({ id, label, value, onChange }: { id: string; label: string; value: number; onChange: (n: number) => void }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box component="label" htmlFor={id} sx={{ fontSize: 12, fontWeight: 600, color: tone.muted }}>
        {label}
      </Box>
      <DenseInput id={id} inputMode="numeric" mono onChange={(e) => onChange(Number(e.target.value.replace(/[^0-9]/g, "")) || 0)} sx={{ width: 54 }} value={value} />
      <Box component="span" sx={{ fontSize: 13 }}>
        min
      </Box>
    </Box>
  );
}

const tableSx = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 12.5,
  "& th": { textAlign: "left", fontSize: 11.5, fontWeight: 600, color: tone.subtle, height: 26, px: "8px", borderBottom: `1px solid ${tone.line}` },
  "& td": { height: 29, px: "8px", borderBottom: `1px solid ${tone.divider}` },
} as const;

/** Time and rest settings (F5). Every table is the server's what-if for the
 * draft; Save sends set_time_settings. */
export function TimeRestSettings({
  view,
  open,
  onClose,
  onSaved,
}: {
  view: TournamentSetupView;
  open: boolean;
  onClose: () => void;
  onSaved: (next: TournamentSetupView) => void;
}) {
  const [time, setTime] = useState<TimeSettings>(view.time);
  const [days, setDays] = useState<SetupDay[]>(view.days);
  const [result, setResult] = useState<SetupWhatIfResult | null>(null);
  const { pending, error, run } = useSetupCommand(view, onSaved);
  const key = JSON.stringify([time, days.map((d) => d.court_count)]);
  useEffect(() => {
    if (!open || !view.days.length) return;
    let active = true;
    const timer = window.setTimeout(() => {
      void whatIfSetup(view.tournament_id, { time, day_courts: days, changes: [] })
        .then((next) => {
          if (active) setResult(next);
        })
        .catch(() => {
          if (active) setResult(null);
        });
    }, 300);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, key]);
  const set = <K extends keyof TimeSettings>(k: K, v: TimeSettings[K]) => setTime((t) => ({ ...t, [k]: v }));
  const impact = result?.impact;
  const capacity = new Map(result?.time_check.days.map((d) => [d.play_date, d.capacity_court_hours]) ?? []);
  const warmupOptions = ["0", "2", "3", "5", "10"].map((v) => ({ value: v, label: v === "0" ? "None" : v }));
  const restOptions = ["5", "10", "15", "20", "30", "45", "60"].map((v) => ({ value: v, label: v }));
  const totalCourts = view.courts.length;

  return (
    <Dialog aria-labelledby="dlg-t" maxWidth={false} onClose={onClose} open={open} slotProps={{ paper: { sx: { width: 1100, maxWidth: "calc(100% - 32px)", borderRadius: "12px", mt: "24px" } } }} sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}>
      <SetupSurface>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", p: "12px 18px" }}>
          <div>
            <Box component="b" id="dlg-t" sx={{ fontSize: 15 }}>
              Time and rest settings
            </Box>
            <Box sx={{ fontSize: 12, color: tone.subtle, mt: "2px" }}>
              Every time check and the schedule use these. Court time per match = warm-up + match length + changeover. Rest uses no court time but limits when a player can play again.
            </Box>
          </div>
          <Box sx={{ flexGrow: 1 }} />
          <DenseButton aria-label="Close" onClick={onClose} size="sm">
            <CloseIcon />
          </DenseButton>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, borderTop: `1px solid ${tone.divider}` }}>
          <Box sx={{ borderRight: `1px solid ${tone.divider}` }}>
            <Section title="MATCH LENGTH, BY EVENT TYPE">
              <Box sx={{ display: "flex", gap: "18px", alignItems: "center" }}>
                <NumberField id="a-s" label="Singles" onChange={(n) => set("singles_match_minutes", n)} value={time.singles_match_minutes} />
                <NumberField id="a-d" label="Doubles" onChange={(n) => set("doubles_match_minutes", n)} value={time.doubles_match_minutes} />
              </Box>
            </Section>
            <Section id="wu-l" title="WARM-UP ON COURT BEFORE EACH MATCH">
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Segmented label="Warm-up on court before each match" outlined onChange={(v) => set("warmup_minutes", Number(v))} options={warmupOptions} value={String(time.warmup_minutes)} />
                <Box component="span" sx={{ fontSize: 12, color: tone.subtle }}>
                  min · part of each match’s court time
                </Box>
              </Box>
            </Section>
            <Section
              id="rest-l"
              title={
                <>
                  MINIMUM REST BETWEEN A PLAYER’S MATCHES <TermHelp label="What is minimum rest?" term="minimum_rest" />
                </>
              }
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <Segmented label="Minimum rest between a player’s matches" outlined onChange={(v) => set("minimum_rest_minutes", Number(v))} options={restOptions} value={String(time.minimum_rest_minutes)} />
                <Box component="span" sx={{ fontSize: 12, color: tone.subtle }}>
                  min
                </Box>
              </Box>
              <Box sx={{ fontSize: 12, color: tone.subtle, mt: "5px" }}>For the whole tournament. Change one event’s rest in its More options.</Box>
            </Section>
            <Section>
              <NumberField id="a-c" label="Changeover between matches" onChange={(n) => set("changeover_minutes", n)} value={time.changeover_minutes} />
            </Section>
            <Section title="COUNT ENTRIES">
              {(["limits", "expected"] as const).map((basis) => (
                <Box component="label" key={basis} sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 13, minHeight: 24 }}>
                  <input checked={time.count_basis === basis} name="cnt" onChange={() => set("count_basis", basis)} style={{ accentColor: tone.brand, width: 16, height: 16, margin: 0 }} type="radio" />
                  {basis === "limits" ? "As if every event fills" : "At the entries you expect"}
                </Box>
              ))}
              <Box sx={{ fontSize: 11.5, color: tone.subtle, lineHeight: 1.35, mt: "2px" }}>
                Consolation counts its worst case: every first-match loser, up to half the bracket.
              </Box>
            </Section>
            <Section title="COURTS EACH DAY">
              <Box component="table" sx={tableSx}>
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Hours</th>
                    <th>Venue</th>
                    <th>Courts</th>
                    <Box component="th" sx={{ textAlign: "right !important" }}>
                      Court-hours
                    </Box>
                  </tr>
                </thead>
                <tbody>
                  {days.map((day, index) => (
                    <tr key={day.play_date}>
                      <Box component="td" sx={{ fontWeight: 600 }}>
                        {formatDay(day.play_date)}
                      </Box>
                      <td>
                        {formatTime(day.first_match_at)} – {formatTime(day.last_match_ends_at)}
                      </td>
                      <td>{view.facility_name}</td>
                      <td>
                        <DenseInput
                          aria-label={`Courts on ${formatDay(day.play_date)}`}
                          inputMode="numeric"
                          mono
                          onChange={(e) => {
                            const n = Number(e.target.value.replace(/[^0-9]/g, ""));
                            setDays((ds) => ds.map((d, i) => (i === index ? { ...d, court_count: n && n < totalCourts ? n : null } : d)));
                          }}
                          sx={{ width: 50, height: 24 }}
                          value={day.court_count ?? totalCourts}
                        />
                      </td>
                      <Box component="td" sx={{ textAlign: "right", fontWeight: 700 }}>
                        <Mono>{capacity.get(day.play_date) ?? "—"}</Mono>
                      </Box>
                    </tr>
                  ))}
                </tbody>
              </Box>
              <Box sx={{ fontSize: 12, color: tone.subtle, mt: "6px" }}>Fewer courts one day? Change that day’s number.</Box>
            </Section>
          </Box>
          <Box>
            <Section title="WHAT WARM-UP DOES TO COURT TIME">
              <Box component="table" sx={tableSx}>
                <thead>
                  <tr>
                    <th>Warm-up</th>
                    <th>Court time per match</th>
                    {(impact?.warmup_rows[0]?.days ?? []).map((d) => (
                      <th key={d.play_date}>{d.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(impact?.warmup_rows ?? []).map((row) => (
                    <Box component="tr" key={row.warmup_minutes} sx={row.current ? { "& td": { bgcolor: tone.brandNote, fontWeight: 700 } } : undefined}>
                      <td>{row.label}</td>
                      <td>
                        <Mono>{row.court_time_label}</Mono>
                      </td>
                      {row.days.map((d) => (
                        <td key={d.play_date}>
                          <Mono sx={{ color: bandText[d.band] }}>
                            {d.percent_label}% {d.band === "plenty" ? "Plenty" : d.band === "tight" ? "Tight" : "Won’t fit"}
                          </Mono>
                        </td>
                      ))}
                    </Box>
                  ))}
                </tbody>
              </Box>
            </Section>
            <Section title="REST CHECK: THE LONGEST DAY FOR ONE PLAYER">
              {impact?.rest_intro ? <Box sx={{ fontSize: 12.5, color: tone.ink2, mb: "6px" }}>{impact.rest_intro}</Box> : null}
              <Box component="table" sx={tableSx}>
                <thead>
                  <tr>
                    <th>Rest</th>
                    <th>Earliest finish</th>
                    <th>{impact?.rest_day_label ?? ""}</th>
                  </tr>
                </thead>
                <tbody>
                  {(impact?.rest_rows ?? []).map((row) => (
                    <Box component="tr" key={row.rest_minutes} sx={row.current ? { "& td": { bgcolor: tone.brandNote, fontWeight: 700 } } : undefined}>
                      <td>{row.rest_minutes} min</td>
                      <td>
                        <Mono sx={{ color: row.fits ? tone.brandText : tone.badText }}>{row.finishes_label}</Mono>
                      </td>
                      <Box component="td" sx={{ color: row.fits ? tone.brandText : tone.badText }}>
                        {row.verdict}
                      </Box>
                    </Box>
                  ))}
                </tbody>
              </Box>
              {impact?.rest_warning ? (
                <Box sx={{ mt: 1, p: "7px 10px", borderRadius: "8px", bgcolor: tone.badSoft, border: `1px solid ${tone.badLine}`, fontSize: 12.5, color: tone.badNoteText }}>
                  {impact.rest_warning} Suggestions: lower the rest, or spread events so fewer players play two events on one day.
                </Box>
              ) : null}
            </Section>
            <Section>
              <Box sx={{ display: "grid", gridTemplateColumns: "210px 1fr", gap: "14px" }}>
                <div>
                  <Box sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".3px", color: tone.subtle, mb: 1 }}>THE BANDS</Box>
                  {[
                    { color: tone.brand, strong: "Up to 75%", text: "Plenty of wiggle room" },
                    { color: tone.warnBar, strong: "75–90%", text: "Tight" },
                    { color: `repeating-linear-gradient(135deg, ${tone.badBar} 0 3px, ${tone.badLine} 3px 5px)`, strong: "Over 90%", text: "Won’t fit" },
                  ].map((band) => (
                    <Box key={band.strong} sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 12.5, mb: "5px" }}>
                      <Box component="span" sx={{ width: 26, height: 6, borderRadius: 3, background: band.color, flex: "none" }} />
                      <span>
                        <b>{band.strong}</b> {band.text}
                      </span>
                    </Box>
                  ))}
                </div>
                <div>
                  <Box sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".3px", color: tone.subtle, mb: 1 }}>HOW ONE EVENT IS COUNTED</Box>
                  <Box sx={{ fontSize: 12, color: tone.ink2, lineHeight: 1.45 }}>{impact?.example_line ?? "Add events to see how one is counted."}</Box>
                </div>
              </Box>
            </Section>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", p: "10px 18px", borderTop: `1px solid ${tone.divider}`, bgcolor: tone.page }}>
          <DenseButton
            onClick={() => {
              setTime(DEFAULTS);
              setDays(view.days.map((d) => ({ ...d, court_count: null })));
            }}
            variant="link"
          >
            Reset to defaults
          </DenseButton>
          <Box sx={{ flexGrow: 1 }} />
          <Box component="span" role={error ? "alert" : undefined} sx={{ fontSize: 12, color: error ? tone.badText : tone.muted }}>
            {error ?? impact?.summary_line ?? ""}
          </Box>
          <DenseButton onClick={onClose}>Cancel</DenseButton>
          <DenseButton
            disabled={pending}
            onClick={async () => {
              if (await run({ command: "set_time_settings", time, day_courts: days } as never)) onClose();
            }}
            variant="primary"
          >
            Save
          </DenseButton>
        </Box>
      </SetupSurface>
    </Dialog>
  );
}
